<script setup>
import { useStore } from 'vuex'
import { computed, onMounted, watch } from 'vue'

import DataTableField from '@/components/form/DataTableField.vue'
import ActionButton from '@/components/common/ActionButton.vue'

const store = useStore()

const props = defineProps({
  selectedItem: {
    type: Number,
    default: null
  },
  addDirty: {
    type: Function,
    required: true
  },
  getDirtyClass: {
    type: Function,
    required: true
  }
})

const xColumns = computed(() => store.state.soilLine.tableHeaders)
const soilLineItems = computed(() => store.state.soilLine.items)
const isLocked = computed(() => store.getters['stabSchematisation/isLockedCurrent'])
const editXCoordinate = (payload) => store.dispatch('soilLine/editXCoordinate', payload)
const addNewXCoordinateBefore = (index) => store.dispatch('soilLine/addNewXCoordinateBefore', index)
const setTableHeaders = () => store.dispatch('soilLine/setTableHeaders')


const isValidXCoordinate = (value) => {
  if (value === null || value === '') return false
  const parsedValue = parseFloat(value)
  if (isNaN(parsedValue)) return false
  return !xColumns.value.some((item) => item === parsedValue)
}

// using to change x column value
const changeXCoordinate = (index, value) => {
  if (isValidXCoordinate(value)) {
    props.addDirty(xColumns.value, value)
    editXCoordinate({ index, xCoordinate: value })
  }
}

const addFirstSoilProfile = () => {
  addNewXCoordinateBefore(0)
}

const rightClick = (e, index) => {
  if (isLocked.value) return
  e.preventDefault()
  const x = e.clientX
  const y = e.clientY
  emit('rightClick', index, x, y)
}

const setHeaderValue = (index, value) => {
  changeXCoordinate(index, value)
  emit('update:selectedItem', null)
}

watch(soilLineItems, () => {
  setTableHeaders()
})
onMounted(() => {
  setTableHeaders()
})

const emit = defineEmits(['rightClick', 'update:selectedItem'])
</script>

<template>
  <thead class="soil-profile-wedge-table-header">
    <tr>
      <th>#</th>
      <th style="min-width: 300px; height: 40px">
        <v-row>
          <v-col style="display: flex; align-items: center"> Soil type </v-col>
          <v-col class="float-right">
            <ActionButton
              v-if="!xColumns.length"
              :disabled="isLocked || !soilLineItems.length"
              prepend-icon="mdi-plus"
              class="float-right ml-2"
              @action="addFirstSoilProfile"
            >
              Add Soil Profile
            </ActionButton>
          </v-col>
        </v-row>
      </th>
      <template v-if="soilLineItems.length">
        <th
          v-for="(coordinate, index) in xColumns"
          :key="index"
          @contextmenu.prevent="rightClick($event, index)"
          @click="emit('update:selectedItem', index)"
          style="min-width: 120px; width: 120px; padding: 0"
          :class="getDirtyClass(xColumns, coordinate.toString())"
        >
          <v-row no-gutters style="height: 40px">
            <v-col cols="3" class="soil-profile-x-header"> X =</v-col>
            <v-col cols="9" style="display: flex; align-items: center">
              <DataTableField
                v-if="selectedItem === index"
                :item="xColumns"
                :parameter="index.toString()"
                :class="[isLocked ? 'disable-events' : '']"
                :font-size="16"
                :validator="isValidXCoordinate"
                commit-on-blur
                @set="setHeaderValue(index, $event.value)"
              />
              <div v-else class="pl-1" style="height: 100%; display: flex; align-items: center">
                <span style="font-size: 16px; font-weight: normal">
                  {{ coordinate }}
                </span>
              </div>
            </v-col>
          </v-row>
        </th>
      </template>
    </tr>
  </thead>
</template>

<style scoped>
.soil-profile-x-header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.soil-profile-wedge-table-header th {
  border-left: 1px solid #dddddd;
}

.soil-profile-wedge-table-header th:last-child {
  border-right: 0 solid #dddddd;
}

.soil-profile-wedge-table-header th:first-child {
  border-left: 0 solid #dddddd;
}
</style>
